/**
 * SRES · CRS Correction Audit Trail viewer (Iter98k · R7 · Feb 2026).
 *
 * Lists every CRS transformation ever applied to a project — surfacing
 * source_epsg, target_epsg, approver, timestamp and source file so any
 * later CP can trace the coordinate lineage.
 *
 * Backend: GET /api/projects/{pid}/crs/audit?limit=N (already existed).
 */
import { useQuery } from "@tanstack/react-query";
import { ClockCounterClockwise, MapPin, User } from "@phosphor-icons/react";
import { getCRSAudit } from "@/lib/api";


export default function CRSAuditTrailPanel({ pid, limit = 50 }) {
  const q = useQuery({
    queryKey: ["crs-audit", pid, limit],
    queryFn:  () => getCRSAudit(pid, limit),
    staleTime: 30_000,
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4"
             data-testid="crs-audit-trail-panel">
      <div className="flex items-center gap-2 mb-3">
        <ClockCounterClockwise size={16} className="text-brand-blue" />
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
          CRS · Correction Audit Trail
        </div>
        <span className="ml-auto text-[11px] font-mono text-slate-500"
                 data-testid="crs-audit-count">
          {(() => {
            const n = q.data?.entries?.length || 0;
            return `${n} ${n === 1 ? "entry" : "entries"}`;
          })()}
        </span>
      </div>
      {q.isLoading && (
        <div className="text-[12px] text-slate-500 animate-pulse"
                 data-testid="crs-audit-loading">
          Loading audit trail…
        </div>
      )}
      {q.isError && (
        <div className="text-[12px] text-rose-700"
                 data-testid="crs-audit-error">
          Unable to load CRS audit trail.
        </div>
      )}
      {!q.isLoading && !q.isError && (q.data?.entries?.length || 0) === 0 && (
        <div className="text-[12px] text-slate-500 italic"
                 data-testid="crs-audit-empty">
          No CRS transformations have been applied to this project.
          Upload a file via the KML CRS Preview to add the first entry.
        </div>
      )}
      {(q.data?.entries || []).length > 0 && (
        <div className="table-wrapper">
          <table className="w-full text-[11.5px]"
                     data-testid="crs-audit-table">
            <thead className="border-b border-slate-200 text-slate-500 uppercase tracking-widest text-[9.5px]">
              <tr>
                <th className="text-left py-1.5">When (UTC)</th>
                <th className="text-left py-1.5">Source EPSG</th>
                <th className="text-left py-1.5">Target EPSG</th>
                <th className="text-left py-1.5" data-mobile-hide="sm">Approver</th>
                <th className="text-left py-1.5" data-mobile-hide="md">Source file</th>
                <th className="text-left py-1.5" data-mobile-hide="xs">Reason</th>
              </tr>
            </thead>
            <tbody>
              {q.data.entries.map((e, i) => (
                <tr key={`${e.timestamp}-${i}`}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`crs-audit-row-${i}`}>
                  <td className="py-1.5 font-mono text-[10.5px] text-slate-600 whitespace-nowrap">
                    {String(e.timestamp || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="py-1.5">
                    <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] text-slate-700">
                      <MapPin size={10} /> {e.source_epsg || "—"}
                    </span>
                  </td>
                  <td className="py-1.5">
                    <span className="inline-flex items-center gap-1 rounded bg-brand-blue/10 px-1.5 py-0.5 font-mono text-[10.5px] text-brand-blue">
                      <MapPin size={10} /> {e.target_epsg || "—"}
                    </span>
                  </td>
                  <td className="py-1.5 text-slate-700" data-mobile-hide="sm">
                    <span className="inline-flex items-center gap-1">
                      <User size={11} className="text-slate-400" />
                      {e.approved_by || "—"}
                    </span>
                  </td>
                  <td className="py-1.5 font-mono text-[10.5px] text-slate-600 max-w-[140px] truncate"
                          data-mobile-hide="md"
                          title={e.source_file || undefined}>
                    {e.source_file || "—"}
                  </td>
                  <td className="py-1.5 text-slate-600 max-w-[220px] truncate"
                          data-mobile-hide="xs"
                          title={e.approval_reason || undefined}>
                    {e.approval_reason || <span className="italic text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
